/**
 * Meter + date formatting — the pure display helpers the usage surfaces share.
 * Bytes use the binary ladder the byte ledger actually speaks (1 GB = 2^30);
 * token counts compact at the thousand/million marks; reset dates render in
 * the viewer's own clock via Intl.
 */

const BYTE_STEPS = [
  { limit: 1024 ** 3, divisor: 1024 ** 3, suffix: "GB" },
  { limit: 1024 ** 2, divisor: 1024 ** 2, suffix: "MB" },
  { limit: 1024, divisor: 1024, suffix: "KB" },
] as const;

/** 2147483648 → "2 GB"; 530 → "530 B". Negative or unknown → "—". */
export function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined || !Number.isFinite(bytes) || bytes < 0) return "—";
  for (const step of BYTE_STEPS) {
    if (bytes >= step.limit) {
      const value = bytes / step.divisor;
      return `${value >= 100 ? Math.round(value) : Math.round(value * 10) / 10} ${step.suffix}`;
    }
  }
  return `${Math.round(bytes)} B`;
}

/** 1234567 → "1.2M"; 8450 → "8.5k"; 742 → "742". */
export function formatCount(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value) || value < 0) return "—";
  if (value >= 1_000_000) return `${Math.round((value / 1_000_000) * 10) / 10}M`;
  if (value >= 1_000) return `${Math.round((value / 1_000) * 10) / 10}k`;
  return `${Math.round(value)}`;
}

/** The unit-aware meter value: bytes through the binary ladder, tokens compact. */
export function formatMeterValue(value: number, unit: "byte" | "token" | "count"): string {
  if (unit === "byte") return formatBytes(value);
  if (unit === "token") return formatCount(value);
  return formatCount(value);
}

/** "2026-10-31T…" → "Oct 31, 2026" (viewer-local). Null → "—". */
export function formatDate(iso: string | null | undefined): string {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  return new Intl.DateTimeFormat("en", { dateStyle: "medium" }).format(date);
}

/** The meter's window rollover: "Oct 31" — the year shows only when it differs. */
export function formatResetAt(iso: string | null | undefined): string {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";
  const now = new Date();
  const sameYear = date.getFullYear() === now.getFullYear();
  return new Intl.DateTimeFormat("en", sameYear ? { month: "short", day: "numeric" } : { dateStyle: "medium" }).format(date);
}

/** The window kind in human words — the meter's period label. */
export function formatWindow(window: "period" | "daily" | null): string {
  if (window === "daily") return "per day";
  if (window === "period") return "per month";
  return "one-time";
}
